import { useEffect, useState } from 'react'
import type { CotizacionTemplateDTO, CotizacionValorDTO, WrapperResponse } from '../../../model/kartera-api-model'
import APIInvoke from '../../../utils/APIInvoke'
import { aseguradoraLabel, errorMessage, planValueFields, ramoFields } from '../common/quote-template-common'

export interface QuoteTemplateDrawerProps {
    templateId: number | null
    onClose: () => void
    onEdit?: (templateId: number) => void
    onDeleted?: (template?: CotizacionTemplateDTO | null) => void
}

export interface TemplateDetailValue {
    key: string
    descripcion: string
    valor: string
}

const DETAIL_ERROR = 'No se pudo cargar el detalle del template.'
const DELETE_ERROR = 'No se pudo eliminar el template.'

const byOrden = (items?: CotizacionValorDTO[] | null): TemplateDetailValue[] =>
    [...(items || [])]
        .sort((left, right) => Number(left.orden ?? 0) - Number(right.orden ?? 0))
        .map((item, index) => ({ key: String(item.id ?? index), descripcion: item.descripcion || '—', valor: item.valor || '—' }))

export const useQuoteTemplateDrawerModel = ({ templateId, onClose, onDeleted }: QuoteTemplateDrawerProps) => {
    const [template, setTemplate] = useState<CotizacionTemplateDTO | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [reloadToken, setReloadToken] = useState(0)
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        if (!templateId) return undefined
        setTemplate(null)
        setError(null)
        setConfirmDelete(false)
        return undefined
    }, [templateId])

    useEffect(() => {
        if (!templateId) return undefined
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                const response = await APIInvoke.invokeGET(`/cotizaciones/templates/${templateId}`)
                const payload: WrapperResponse<CotizacionTemplateDTO> | null = await response.json()
                if (!active) return
                if (!payload?.ok || !payload.body) throw new Error(payload?.message || DETAIL_ERROR)
                setTemplate(payload.body)
            } catch (loadError) {
                if (active) {
                    setTemplate(null)
                    setError(errorMessage(loadError, DETAIL_ERROR))
                }
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [templateId, reloadToken])

    const deleteTemplate = async () => {
        if (!template?.id || deleting) return
        try {
            setConfirmDelete(false)
            setDeleting(true)
            const payload: WrapperResponse<CotizacionTemplateDTO> | null = await (await APIInvoke.invokeDELETE(`/cotizaciones/templates/${template.id}`)).json()
            if (!payload?.ok) throw new Error(payload?.message || DELETE_ERROR)
            onDeleted?.(template)
            onClose()
        } catch (deleteError) {
            setError(errorMessage(deleteError, DELETE_ERROR))
        } finally {
            setDeleting(false)
        }
    }

    return {
        template,
        loading,
        error,
        aseguradora: aseguradoraLabel(template?.aseguradora) || '—',
        moneda: template?.moneda?.abreviacion || '—',
        campos: template ? planValueFields(template.ramo).map((field) => ({ label: field.label, value: template[field.key] || '' })) : [],
        showCaracteristicas: ramoFields(template?.ramo).caracteristicas,
        caracteristicas: byOrden(template?.caracteristicas),
        coberturas: byOrden(template?.coberturas),
        confirmDelete,
        deleting,
        reload: () => setReloadToken((token) => token + 1),
        askDelete: () => setConfirmDelete(true),
        cancelDelete: () => setConfirmDelete(false),
        deleteTemplate,
    }
}
